import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  Fab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Chip,
  Badge,
  Collapse,
  Alert,
  Snackbar,
} from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MinimizeRoundedIcon from "@mui/icons-material/MinimizeRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PushPinRoundedIcon from "@mui/icons-material/PushPinRounded";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

import AssistantIcon from "./AssistantIcon";
import ChatResultTable from "./ChatResultTable";
import ChatChart from "./ChatChart";
import PinnedQueries from "./PinnedQueries";
import config from "./chatAssistantConfig";

const API = process.env.REACT_APP_API_BASE_URL;

const DEFAULT_SERVER_CONFIG = {
  enabled: true,
  assistantName: config.ASSISTANT_NAME,
  greeting:
    "Hi {name}! I'm MatchIQ. Ask me anything about demand, supply, PDP, VCDP, rotation, NBL, lateral hiring or training data. I'll build the query and show you the result - as a table or as a chart.",
  suggestions: [],
  maxQuestionLength: 500,
  showGeneratedSql: true,
  pinsEnabled: true,
  maxPinsPerUser: 50,
};

/**
 * Request headers including the bearer token the rest of the app uses.
 * The server checks it according to nlqSettings.json -> auth.mode.
 */
function authHeaders() {
  const headers = { "Content-Type": "application/json" };
  const token = localStorage.getItem("token");
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/** Renders **bold** segments; the assistant uses them for scalar answers. */
function RichText({ text }) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i}>{part.slice(2, -2)}</strong>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

/**
 * "Vikram Mehta" -> "Vikram". Greetings read better with a first name, and the
 * header already shows the full one.
 */
function firstName(name) {
  const cleaned = String(name || "").trim();
  if (!cleaned) return "";
  // The employees API returns "LastName,FirstName"; App.js normally flips it,
  // but handle the raw form too in case it is passed straight through.
  if (cleaned.includes(",")) {
    const parts = cleaned.split(",");
    return parts[1] ? parts[1].trim().split(/\s+/)[0] : parts[0].trim();
  }
  return cleaned.split(/\s+/)[0];
}

/**
 * Substitutes the signed-in user's name into the configured greeting.
 *
 * The greeting text lives on the server so it can be reworded without a UI
 * rebuild, with {name} marking where the name goes. When the name has not
 * loaded yet, "User" is used in place of the name rather than leaving it blank.
 */
function personaliseGreeting(greeting, userName) {
  const text = String(greeting || "");
  const name = firstName(userName) || "User";
  if (!text.includes("{name}")) {
    return `Hi ${name}! ${text}`;
  }
  return text.replace(/\{name\}/g, name);
}

/** One chat bubble. */
function Message({ msg, pinsEnabled, showSql: sqlPanelEnabled, pinned, pinBusy, onTogglePin }) {
  const isUser = msg.role === "user";
  const [showSql, setShowSql] = useState(false);
  const isError = msg.kind === "error";

  if (isUser) {
    return (
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.25 }}>
        <Paper
          elevation={0}
          sx={{
            px: 1.25,
            py: 0.75,
            maxWidth: "85%",
            bgcolor: config.COLORS.primary,
            color: "#fff",
            borderRadius: "12px 12px 2px 12px",
          }}
        >
          <Typography variant="body2" sx={{ fontSize: "0.8rem", whiteSpace: "pre-wrap" }}>
            {msg.content}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Pinning needs the question that produced this answer, so an answer that
  // arrived without one (the opening greeting, a transport error) cannot be
  // pinned.
  const canPin = pinsEnabled && !isError && Boolean(msg.question);

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1.25 }}>
      <Box sx={{ maxWidth: "95%", width: msg.table || msg.chart ? "100%" : "auto" }}>
        <Paper
          elevation={0}
          sx={{
            px: 1.25,
            py: 0.75,
            bgcolor: isError ? "rgba(211,47,47,.09)" : "action.hover",
            border: "1px solid",
            borderColor: isError ? "rgba(211,47,47,.28)" : "divider",
            borderRadius: "12px 12px 12px 2px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.8rem",
                whiteSpace: "pre-wrap",
                color: isError ? "error.main" : "text.primary",
                display: "flex",
                gap: 0.5,
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              {isError && (
                <ErrorOutlineRoundedIcon sx={{ fontSize: "1rem", mt: "1px", flexShrink: 0 }} />
              )}
              <span>
                <RichText text={msg.content} />
              </span>
            </Typography>

            {canPin && (
              <Tooltip title={pinned ? "Remove from pinned questions" : "Pin this question"}>
                <IconButton
                  size="small"
                  onClick={() => onTogglePin(msg)}
                  disabled={pinBusy}
                  aria-label={pinned ? "Remove pin" : "Pin this question"}
                  sx={{
                    p: 0.3,
                    flexShrink: 0,
                    color: pinned ? config.COLORS.primary : "text.disabled",
                  }}
                >
                  {pinned ? (
                    <PushPinRoundedIcon sx={{ fontSize: "0.95rem" }} />
                  ) : (
                    <PushPinOutlinedIcon sx={{ fontSize: "0.95rem" }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {msg.chart && msg.table && (
            <ChatChart
              title={msg.table.title}
              chart={msg.chart}
              columns={msg.table.columns}
              rows={msg.table.rows}
              allRows={msg.table.allRows}
              rowCount={msg.table.rowCount}
              truncated={msg.table.truncated}
              hitCeiling={msg.table.hitCeiling}
            />
          )}

          {!msg.chart && msg.table && (
            <ChatResultTable
              title={msg.table.title}
              columns={msg.table.columns}
              rows={msg.table.rows}
              allRows={msg.table.allRows}
              rowCount={msg.table.rowCount}
              truncated={msg.table.truncated}
              hitCeiling={msg.table.hitCeiling}
            />
          )}

          {msg.sql && sqlPanelEnabled && (
            <Box sx={{ mt: 0.5 }}>
              <Button
                size="small"
                onClick={() => setShowSql((v) => !v)}
                startIcon={<CodeRoundedIcon sx={{ fontSize: "0.9rem" }} />}
                sx={{
                  textTransform: "none",
                  fontSize: "0.65rem",
                  py: 0,
                  px: 0.5,
                  minWidth: "auto",
                  color: "text.secondary",
                }}
              >
                {showSql ? "Hide query" : "View query"}
              </Button>
              <Collapse in={showSql}>
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 0.75,
                    bgcolor: "#1e2a35",
                    color: "#d6e4f0",
                    borderRadius: 1,
                    fontSize: "0.63rem",
                    lineHeight: 1.45,
                    overflowX: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "Consolas, Monaco, monospace",
                  }}
                >
                  {msg.sql}
                </Box>
              </Collapse>
            </Box>
          )}
        </Paper>
        {msg.elapsedMs != null && (
          <Typography
            variant="caption"
            sx={{ ml: 0.5, color: "text.disabled", fontSize: "0.6rem" }}
          >
            {(msg.elapsedMs / 1000).toFixed(1)}s
            {msg.fromPin ? " · pinned question" : ""}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function ChatAssistant({ activePage, role, userName, externalLauncher = false, openRequest = 0, onOpenChange }) {
  const [serverConfig, setServerConfig] = useState(DEFAULT_SERVER_CONFIG);
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [introActive, setIntroActive] = useState(false);
  const introTimerRef = useRef(null);

  /** An answer arrived while the window was shut or minimised. */
  const [unread, setUnread] = useState(false);

  const [showPins, setShowPins] = useState(false);
  const [pins, setPins] = useState([]);
  const [pinsLoading, setPinsLoading] = useState(false);
  const [pinsError, setPinsError] = useState(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // `busy` as a ref as well as state: runPin can fall through to send() inside
  // the same tick, before a setBusy(false) has been applied, and send()'s guard
  // has to see the new value or the re-ask would be silently dropped.
  const busyRef = useRef(false);
  const setBusyState = useCallback((value) => {
    busyRef.current = value;
    setBusy(value);
  }, []);

  // `send` is async, so by the time an answer lands the state it closed over is
  // stale. This ref always reflects whether the transcript is actually on
  // screen, which is what decides if the answer counts as unread.
  const visibleRef = useRef(false);
  useEffect(() => {
    visibleRef.current = open && !minimized;
  }, [open, minimized]);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useEffect(() => () => window.clearTimeout(introTimerRef.current), []);

  const pinsEnabled =
    config.PINS_ENABLED && (!serverConfig || serverConfig.pinsEnabled !== false);

  // Both switches must allow it. The server one is the real control - with it
  // off, `sql` never reaches the browser in the first place.
  const sqlPanelEnabled =
    config.SHOW_SQL_PANEL && (!serverConfig || serverConfig.showGeneratedSql !== false);

  /* ---- load server config once (also tells us if the API is switched off) */
  useEffect(() => {
    if (!config.ENABLED) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/nlq/config`);
        if (!res.ok) {
          throw new Error(`Config request failed (${res.status})`);
        }
        const data = await res.json();
        if (!cancelled) setServerConfig(data);
      } catch (err) {
        if (!cancelled) setServerConfig(DEFAULT_SERVER_CONFIG);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---- keep the transcript scrolled to the newest message */
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy, open, minimized, maximized, showPins]);

  /* ------------------------------------------------------------------ pins */

  const loadPins = useCallback(async () => {
    if (!pinsEnabled) return;
    setPinsLoading(true);
    setPinsError(null);
    try {
      const res = await fetch(`${API}/nlq/pins`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not load your pins.");
      setPins(Array.isArray(data.pins) ? data.pins : []);
    } catch (err) {
      setPins([]);
      setPinsError(err.message || "Could not load your pinned questions.");
    } finally {
      setPinsLoading(false);
    }
  }, [pinsEnabled]);

  // Loaded once the user actually opens the chat, so a page view that never
  // touches the assistant makes no extra request.
  useEffect(() => {
    if (open && pinsEnabled) loadPins();
  }, [open, pinsEnabled, loadPins]);

  const sameQuestion = (a, b) =>
    String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

  const findPin = (question) => pins.find((p) => sameQuestion(p.question, question));

  const togglePin = async (msg) => {
    const existing = findPin(msg.question);
    setPinBusy(true);
    try {
      if (existing) {
        const res = await fetch(`${API}/nlq/pins/${existing.id}`, {
          method: "DELETE",
          headers: authHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Could not remove that pin.");
        setPins((list) => list.filter((p) => p.id !== existing.id));
        setToast({ severity: "info", text: "Pin removed." });
      } else {
        const res = await fetch(`${API}/nlq/pins`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            question: msg.question,
            title: msg.table ? msg.table.title : msg.title || "",
            sql: msg.sql || null,
            renderMode: msg.renderMode || "table",
            chart: msg.chart || null,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Could not pin that question.");
        setPins((list) => [data.pin, ...list.filter((p) => p.id !== data.pin.id)]);
        setToast({
          severity: "success",
          text: "Pinned. Open the pin list from the title bar any time.",
        });
      }
    } catch (err) {
      setToast({ severity: "error", text: err.message });
    } finally {
      setPinBusy(false);
    }
  };

  const deletePin = async (pin) => {
    setPinBusy(true);
    try {
      const res = await fetch(`${API}/nlq/pins/${pin.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not remove that pin.");
      setPins((list) => list.filter((p) => p.id !== pin.id));
    } catch (err) {
      setToast({ severity: "error", text: err.message });
    } finally {
      setPinBusy(false);
    }
  };

  /* ----------------------------------------------------------------- asking */

  /** Appends the assistant turn for one answer envelope. */
  const appendAnswer = useCallback((data, question, fromPin) => {
    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        kind: data.type === "error" ? "error" : "answer",
        content: data.message || "I did not get a usable answer for that.",
        historyText: data.message,
        question,
        fromPin: Boolean(fromPin),
        title: data.title,
        renderMode: data.type === "chart" ? "chart" : data.type === "text" ? "text" : "table",
        chart: data.type === "chart" ? data.chart : null,
        sql: data.sql || null,
        elapsedMs: data.elapsedMs,
        table:
          data.type === "table" || data.type === "chart"
            ? {
                title: data.title,
                columns: data.columns,
                rows: data.rows,
                allRows: data.allRows,
                rowCount: data.rowCount,
                truncated: data.truncated,
                hitCeiling: data.hitCeiling,
              }
            : null,
      },
    ]);
    if (!visibleRef.current) setUnread(true);
  }, []);

  const send = async (text) => {
    const question = (text != null ? text : input).trim();
    if (!question || busyRef.current) return;

    const maxLen = (serverConfig && serverConfig.maxQuestionLength) || 500;
    if (question.length > maxLen) {
      setMessages((m) => [
        ...m,
        { role: "user", content: question },
        {
          role: "assistant",
          kind: "error",
          content: `That question is a bit long. Please keep it under ${maxLen} characters.`,
        },
      ]);
      setInput("");
      return;
    }

    // Replay only plain question/answer text as history - never tables or SQL.
    const history = messages
      .filter((m) => !m.kind || m.kind !== "error")
      .slice(-config.MAX_CLIENT_HISTORY_TURNS * 2)
      .map((m) => ({ role: m.role, content: m.historyText || m.content }));

    setShowPins(false);
    setMessages((m) => [...m, { role: "user", content: question }]);
    setInput("");
    setBusyState(true);

    try {
      const res = await fetch(`${API}/nlq/query`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ question, history }),
      });

      const data = await res.json();

      if (!res.ok && !data.message) {
        throw new Error(data.error || `Request failed (${res.status})`);
      }

      appendAnswer(data, question, false);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          kind: "error",
          content:
            "I could not reach the assistant service. Please check your connection and try again.",
        },
      ]);
      if (!visibleRef.current) setUnread(true);
    } finally {
      setBusyState(false);
      // Return focus so the user can keep typing.
      window.setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
    }
  };

  /**
   * Opens a pinned question. The stored SQL is re-run server-side, so this is
   * fast and free; if the pin has no SQL (a pinned conversational answer) or the
   * stored SQL no longer validates, the server asks us to put the question
   * through the model again.
   */
  const runPin = async (pin) => {
    if (busyRef.current) return;
    setShowPins(false);
    setMessages((m) => [...m, { role: "user", content: pin.question }]);
    setBusyState(true);

    try {
      const res = await fetch(`${API}/nlq/pins/${pin.id}/run`, {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await res.json();

      if (!res.ok && !data.message) {
        throw new Error(data.error || `Request failed (${res.status})`);
      }

      if (data.type === "reask") {
        setBusyState(false);
        // Drop the echo we just added - send() adds the question itself.
        setMessages((m) => m.slice(0, -1));
        await send(pin.question);
        return;
      }

      appendAnswer(data, pin.question, true);
      setPins((list) =>
        list.map((p) =>
          p.id === pin.id
            ? { ...p, runCount: (p.runCount || 0) + 1, lastRunAt: new Date().toISOString() }
            : p
        )
      );
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          kind: "error",
          content: "I could not open that pinned question. Please try again.",
        },
      ]);
      if (!visibleRef.current) setUnread(true);
    } finally {
      setBusyState(false);
    }
  };

  /* ------------------------------------------------------------ window state */

  const resetSession = useCallback((turns) => {
    // Best-effort notification; the session lives in this component's state,
    // so clearing it here is what actually ends it. Pins are deliberately NOT
    // cleared - they belong to the user's account, not to the session.
    try {
      fetch(`${API}/nlq/session/end`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ turns }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) {
      /* ignore */
    }
    setMessages([]);
    setInput("");
    setBusyState(false);
    setMinimized(false);
    setMaximized(false);
    setShowPins(false);
    setUnread(false);
    setOpen(false);
  }, [setBusyState]);

  const handleCloseRequest = () => {
    // Nothing said yet - no need to warn about losing a conversation.
    if (messages.length === 0) {
      resetSession(0);
      return;
    }
    setConfirmClose(true);
  };

  /** Opening or restoring the window is what marks the answer as seen. */
  const showWindow = () => {
    if (!open) {
      setIntroActive(true);
      window.clearTimeout(introTimerRef.current);
      introTimerRef.current = window.setTimeout(() => setIntroActive(false), 3600);
    }
    setOpen(true);
    setMinimized(false);
    setUnread(false);
    window.setTimeout(() => inputRef.current && inputRef.current.focus(), 180);
  };

  useEffect(() => {
    if (openRequest > 0) showWindow();
    // openRequest is an intentional command counter from the sidebar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  /* -------------------------------------------------------- visibility gate */

  if (!config.ENABLED) return null;
  if (serverConfig && serverConfig.enabled === false) return null;

  const page = String(activePage || "").toLowerCase();
  if (config.HIDDEN_ON_PAGES.map((p) => p.toLowerCase()).includes(page)) return null;

  if (
    config.VISIBLE_TO_ROLES.length > 0 &&
    !config.VISIBLE_TO_ROLES.map((r) => r.toLowerCase()).includes(
      String(role || "").toLowerCase()
    )
  ) {
    return null;
  }

  const { WINDOW, LAUNCHER, COLORS } = config;
  const width = maximized ? WINDOW.maximizedWidth : WINDOW.normalWidth;
  const height = maximized ? WINDOW.maximizedHeight : WINDOW.normalHeight;
  const suggestions = (serverConfig && serverConfig.suggestions) || [];
  const assistantName =
    (serverConfig && serverConfig.assistantName) || config.ASSISTANT_NAME;
  const greeting = personaliseGreeting(serverConfig.greeting, userName);

  return (
    <>
      {/* ---------------------------------------- launcher (bottom-right) */}
      {!externalLauncher && !open && (
        <Tooltip
          title={unread ? `${assistantName} has your answer` : `Ask ${assistantName}`}
          placement="left"
        >
          <Badge
            color="error"
            variant="dot"
            invisible={!unread}
            overlap="circular"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              position: "fixed",
              bottom: LAUNCHER.bottom,
              right: LAUNCHER.right,
              zIndex: 1300,
              "& .MuiBadge-dot": {
                width: 12,
                height: 12,
                borderRadius: "50%",
                border: "2px solid #fff",
              },
            }}
          >
            <Fab
              onClick={showWindow}
              aria-label={`Open ${assistantName}, the Demand and Supply assistant`}
              sx={{
                // White, because the icon is a full-colour brand mark with its
                // own blue disc - a blue Fab behind it would merge with it.
                bgcolor: "#fff",
                width: 56,
                height: 56,
                boxShadow: "0 6px 18px rgba(0,94,184,0.32)",
                "&:hover": {
                  bgcolor: "#f3f8ff",
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 24px rgba(0,94,184,0.42)",
                },
                transition: "transform .2s ease, box-shadow .2s ease",
              }}
            >
              <AssistantIcon sx={{ fontSize: "2.5rem" }} />
            </Fab>
          </Badge>
        </Tooltip>
      )}

      {/* ------------------------------------------ chat window (bottom-left) */}
      {open && (
        <Paper
          elevation={10}
          sx={{
            position: "fixed",
            bottom: WINDOW.bottom,
            left: WINDOW.left,
            width: minimized ? 320 : width,
            height: minimized ? "auto" : height,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 48px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 2,
            zIndex: 1300,
            transition: "width .25s ease, height .25s ease, background-color .25s ease",
            animation: "matchiqWindowIn .38s cubic-bezier(.2,.9,.2,1) both",
            "@keyframes matchiqWindowIn": {
              from: { opacity: 0, transform: "translate(-20px, 28px) scale(.94)" },
              to: { opacity: 1, transform: "translate(0, 0) scale(1)" },
            },
          }}
        >
          {introActive && !minimized && (
            <Box sx={{
              position: "absolute", inset: 0, zIndex: 20, overflow: "hidden",
              bgcolor: "background.paper",
              backgroundImage: "radial-gradient(circle at 50% 45%, rgba(46,123,246,.16), transparent 42%), linear-gradient(145deg, rgba(235,247,255,.72), rgba(255,255,255,.96))",
              animation: "matchiqIntroFade 3.6s ease both",
              "@keyframes matchiqIntroFade": { "0%,88%": { opacity: 1 }, "100%": { opacity: 0, pointerEvents: "none" } },
              "@keyframes matchiqHandWave": { "0%,100%": { transform: "rotate(0deg)" }, "18%": { transform: "rotate(-19deg)" }, "36%": { transform: "rotate(12deg)" }, "54%": { transform: "rotate(-17deg)" }, "72%": { transform: "rotate(9deg)" } },
            }}>
              <Box sx={{ position: "absolute", width: 176, height: 176, left: "calc(50% - 88px)", top: "calc(47% - 88px)", border: "1px solid rgba(46,123,246,.16)", borderRadius: "50%", animation: "matchiqRipple 2.2s ease-out .25s both", "&::after": { content: '""', position: "absolute", inset: 22, border: "1px dashed rgba(31,181,214,.28)", borderRadius: "50%" }, "@keyframes matchiqRipple": { from: { opacity: 0, transform: "scale(.55) rotate(0deg)" }, "35%,75%": { opacity: 1 }, to: { opacity: 0, transform: "scale(1.38) rotate(50deg)" } } }} />

              <Box sx={{ position: "absolute", zIndex: 4, left: "50%", top: "calc(47% - 155px)", minWidth: 165, textAlign: "center", px: 2, py: 1.05, borderRadius: "18px 18px 5px 18px", bgcolor: "white", border: "1px solid rgba(46,123,246,.16)", boxShadow: "0 12px 32px rgba(6,40,127,.12)", "&::after": { content: '""', position: "absolute", left: "calc(50% - 7px)", bottom: -7, width: 13, height: 13, bgcolor: "white", borderRight: "1px solid rgba(46,123,246,.16)", borderBottom: "1px solid rgba(46,123,246,.16)", transform: "rotate(45deg)" }, animation: "matchiqHelloBubble 2.65s cubic-bezier(.2,.85,.25,1) both", "@keyframes matchiqHelloBubble": { "0%,12%": { opacity: 0, transform: "translate(-50%, 14px) scale(.82)" }, "28%,76%": { opacity: 1, transform: "translate(-50%, 0) scale(1)" }, "100%": { opacity: 0, transform: "translate(-50%, -8px) scale(.96)" } } }}>
                <Typography sx={{ fontWeight: 850, color: "#0b4f85", lineHeight: 1.2 }}>
                  Hello, {firstName(userName) || "there"}!{" "}
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      animation: "matchiqEmojiWave .65s ease-in-out 3",
                      transformOrigin: "70% 75%",
                      "@keyframes matchiqEmojiWave": {
                        "0%,100%": { transform: "rotate(0deg)" },
                        "50%": { transform: "rotate(24deg)" },
                      },
                    }}
                  >
                    &#128075;
                  </Box>
                </Typography>
                <Typography variant="caption" color="text.secondary">Nice to see you.</Typography>
              </Box>

              <Box sx={{ position: "absolute", zIndex: 2, display: "grid", placeItems: "center", left: "calc(50% - 56px)", bottom: "calc(53% - 70px)", animation: "matchiqMascotMove 3.6s cubic-bezier(.45,0,.22,1) both", "@keyframes matchiqMascotMove": { "0%": { opacity: 0, transform: "translateY(22px) scale(.55) rotate(-7deg)" }, "12%": { opacity: 1, transform: "translateY(0) scale(1.08) rotate(3deg)" }, "24%,62%": { opacity: 1, transform: "translateY(0) scale(1) rotate(0deg)" }, "72%": { left: "calc(50% - 56px)", bottom: "calc(53% - 70px)", transform: "scale(1)" }, "100%": { left: 10, bottom: 4, transform: "scale(.34)", opacity: .25 } } }}>
                <AssistantIcon animated sx={{ fontSize: 112, filter: "drop-shadow(0 17px 25px rgba(6,40,127,.28))", "& .matchiq-wave-arm": { transformBox: "fill-box", transformOrigin: "10% 90%", animation: "matchiqHandWave .62s ease-in-out .55s 3" } }} />
                <Typography sx={{ mt: .8, fontWeight: 750, color: "text.secondary", whiteSpace: "nowrap", animation: "matchiqLabelFade 3.05s ease both", "@keyframes matchiqLabelFade": { "0%,32%": { opacity: 0, transform: "translateY(8px)" }, "48%,78%": { opacity: 1, transform: "translateY(0)" }, "100%": { opacity: 0, transform: "translateY(-5px)" } } }}>MatchIQ is ready to help</Typography>
              </Box>

              {[[-76, -42, 0], [76, 30, .25], [-88, 58, .5], [92, -54, .75]].map(([x, y, delay], index) => <Box key={index} sx={{ position: "absolute", left: `calc(50% + ${x}px)`, top: `calc(47% + ${y}px)`, width: 7, height: 7, borderRadius: index % 2 ? "50%" : 1, bgcolor: index % 2 ? "#45c9dd" : "#2e7bf6", animation: `matchiqSparkle 1.25s ease-in-out ${delay}s 2 both`, "@keyframes matchiqSparkle": { "0%,100%": { opacity: 0, transform: "scale(.3) rotate(0deg)" }, "50%": { opacity: .8, transform: "scale(1) rotate(45deg)" } } }} />)}
            </Box>
          )}
          {/* --- title bar --- */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.accent} 100%)`,
              color: "#fff",
              flexShrink: 0,
              cursor: minimized ? "pointer" : "default",
            }}
            onClick={minimized ? showWindow : undefined}
          >
            {/* While minimised the title bar is the only thing on screen, so the
                unread dot has to live here too. */}
            <Badge
              color="error"
              variant="dot"
              invisible={!(minimized && unread)}
              overlap="circular"
              sx={{
                "& .MuiBadge-dot": {
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  border: "1.5px solid #fff",
                },
              }}
            >
              <AssistantIcon sx={{ fontSize: "1.3rem", display: "block" }} />
            </Badge>

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, fontSize: "0.82rem", flexGrow: 1, minWidth: 0 }}
              noWrap
            >
              {assistantName}
              {minimized && unread ? " · new answer" : ""}
            </Typography>

            {!minimized && pinsEnabled && (
              <Tooltip title={showPins ? "Back to the conversation" : "Pinned questions"}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setShowPins((v) => !v);
                    if (!showPins) loadPins();
                  }}
                  sx={{ color: "#fff", p: 0.35 }}
                  aria-label={showPins ? "Back to the conversation" : "Show pinned questions"}
                >
                  {showPins ? (
                    <ArrowBackRoundedIcon sx={{ fontSize: "1rem" }} />
                  ) : (
                    <Badge
                      badgeContent={pins.length}
                      color="default"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "0.55rem",
                          height: 13,
                          minWidth: 13,
                          bgcolor: "#fff",
                          color: COLORS.primaryDark,
                          fontWeight: 700,
                        },
                      }}
                    >
                      <PushPinRoundedIcon sx={{ fontSize: "1rem" }} />
                    </Badge>
                  )}
                </IconButton>
              </Tooltip>
            )}

            {!minimized && (
              <Tooltip title={maximized ? "Restore size" : "Maximise"}>
                <IconButton
                  size="small"
                  onClick={() => setMaximized((v) => !v)}
                  sx={{ color: "#fff", p: 0.35 }}
                  aria-label={maximized ? "Restore chat size" : "Maximise chat"}
                >
                  {maximized ? (
                    <CloseFullscreenRoundedIcon sx={{ fontSize: "0.95rem" }} />
                  ) : (
                    <OpenInFullRoundedIcon sx={{ fontSize: "0.95rem" }} />
                  )}
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={minimized ? "Expand" : "Minimise"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (minimized) {
                    showWindow();
                  } else {
                    setMinimized(true);
                  }
                }}
                sx={{ color: "#fff", p: 0.35 }}
                aria-label={minimized ? "Expand chat" : "Minimise chat"}
              >
                {minimized ? (
                  <OpenInFullRoundedIcon sx={{ fontSize: "0.95rem" }} />
                ) : (
                  <MinimizeRoundedIcon sx={{ fontSize: "0.95rem" }} />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Close chat and end session">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseRequest();
                }}
                sx={{ color: "#fff", p: 0.35 }}
                aria-label="Close chat"
              >
                <CloseRoundedIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          </Box>

          {!minimized && (
            <>
              {/* --- transcript (or the pin list) --- */}
              <Box
                ref={scrollRef}
                sx={{
                  flexGrow: 1,
                  overflowY: "auto",
                  px: 1.25,
                  py: 1.25,
                  bgcolor: "background.default",
                }}
              >
                {showPins ? (
                  <PinnedQueries
                    pins={pins}
                    loading={pinsLoading}
                    error={pinsError}
                    busy={busy || pinBusy}
                    maxPins={(serverConfig && serverConfig.maxPinsPerUser) || 50}
                    onRun={runPin}
                    onDelete={deletePin}
                  />
                ) : (
                  <>
                    {messages.length === 0 && (
                      <Box>
                        <Box sx={{ display: "flex", gap: 0.75, mb: 1.25 }}>
                          <AssistantIcon
                            sx={{ fontSize: "1.9rem", flexShrink: 0, mt: "-2px" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                          >
                            {greeting}
                          </Typography>
                        </Box>
                        {suggestions.length > 0 && (
                          <>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mb: 0.5,
                                fontWeight: 700,
                                color: "text.secondary",
                                fontSize: "0.65rem",
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                              }}
                            >
                              Try one of these
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {suggestions.map((s) => (
                                <Chip
                                  key={s}
                                  label={s}
                                  size="small"
                                  onClick={() => send(s)}
                                  sx={{
                                    fontSize: "0.68rem",
                                    height: "auto",
                                    py: 0.4,
                                    cursor: "pointer",
                                    borderColor: `${COLORS.primary}55`,
                                    color: COLORS.primaryDark,
                                    "& .MuiChip-label": {
                                      whiteSpace: "normal",
                                      px: 0.75,
                                      lineHeight: 1.35,
                                    },
                                    "&:hover": { bgcolor: `${COLORS.primary}12` },
                                  }}
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </>
                        )}
                      </Box>
                    )}

                    {messages.map((m, i) => (
                      <Message
                        key={i}
                        msg={m}
                        pinsEnabled={pinsEnabled}
                        showSql={sqlPanelEnabled}
                        pinned={Boolean(m.question && findPin(m.question))}
                        pinBusy={pinBusy}
                        onTogglePin={togglePin}
                      />
                    ))}

                    {busy && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                        <CircularProgress size={13} thickness={5} />
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontSize: "0.7rem" }}
                        >
                          Building the query and fetching your data…
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {/* --- composer --- */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 0.75,
                  p: 1,
                  borderTop: "1px solid #e6ebf1",
                  bgcolor: "background.paper",
                  flexShrink: 0,
                }}
              >
                <Tooltip title="MatchIQ assistant"><Box sx={{ width: 38, height: 38, flexShrink: 0, display: "grid", placeItems: "center", borderRadius: "50%", bgcolor: "background.default" }}><AssistantIcon sx={{ fontSize: "2rem" }} /></Box></Tooltip>
                <TextField
                  inputRef={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about demand, supply, PDP, VCDP…"
                  size="small"
                  fullWidth
                  multiline
                  maxRows={4}
                  disabled={busy}
                  inputProps={{ "aria-label": "Your question" }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.8rem",
                      borderRadius: 2,
                      bgcolor: "background.default",
                    },
                  }}
                />
                <Tooltip title="Send">
                  <span>
                    <IconButton
                      onClick={() => send()}
                      disabled={busy || !input.trim()}
                      aria-label="Send question"
                      sx={{
                        bgcolor: COLORS.primary,
                        color: "#fff",
                        width: 34,
                        height: 34,
                        "&:hover": { bgcolor: COLORS.primaryDark },
                        "&.Mui-disabled": { bgcolor: "#cfd8e3", color: "#fff" },
                      }}
                    >
                      <SendRoundedIcon sx={{ fontSize: "1.05rem" }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </>
          )}
        </Paper>
      )}

      {/* ------------------------------------------ close confirmation */}
      <Dialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700, pb: 1 }}>
          End this chat session?
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <DialogContentText sx={{ fontSize: "0.85rem" }}>
            Closing the chat ends your session and clears this conversation. Any
            results still on screen will be lost.
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 1.5, fontSize: "0.75rem", py: 0.25 }}>
            Download any result you still need before closing. Choose
            <strong> No </strong> to keep the conversation, or minimise it
            instead to leave it running in the background.
            {pinsEnabled ? " Pinned questions are kept either way." : ""}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmClose(false)}
            sx={{ textTransform: "none" }}
          >
            No, keep it open
          </Button>
          <Button
            onClick={() => {
              setConfirmClose(false);
              resetSession(messages.filter((m) => m.role === "user").length);
            }}
            variant="contained"
            autoFocus
            sx={{
              textTransform: "none",
              bgcolor: config.COLORS.primary,
              "&:hover": { bgcolor: config.COLORS.primaryDark },
            }}
          >
            Yes, end session
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast ? toast.severity : "success"}
          variant="filled"
          onClose={() => setToast(null)}
          sx={{ fontSize: "0.78rem" }}
        >
          {toast ? toast.text : ""}
        </Alert>
      </Snackbar>
    </>
  );
}
